/**
 * Small wrapper around the theme REST API.
 *
 * @package AGDS
 * @category Modules
 * @module ApiService
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 */
class ApiService {
    /**
     * Creates an API service instance.
     *
     * @param {string} baseUrl - API base URL.
     * @param {string} nonce - WordPress REST nonce.
     */
    constructor(baseUrl, nonce) {
        this.baseUrl = baseUrl;
        this.nonce = nonce;
    }

    /**
     * Sends a GET request.
     *
     * @param {string} endpoint - Request endpoint.
     * @param {object} params - Query parameters.
     * @returns {Promise<object>}
     */
    async get(endpoint, params = {}) {
        const url = new URL(this.baseUrl + endpoint);

        Object.entries(params || {}).forEach(([key, value]) => {
            if (value === undefined || value === null) return;

            if (Array.isArray(value)) {
                value.forEach((v) => url.searchParams.append(key, String(v)));
            } else {
                url.searchParams.append(key, String(value));
            }
        });

        const options = {
            method: "GET",
            headers: {
                "X-WP-Nonce": this.nonce,
                "X-Requested-With": "XMLHttpRequest",
            },
        };

        return this.request(url, options);
    }

    /**
     * Sends a POST request.
     *
     * @param {string} endpoint - Request endpoint.
     * @param {object|FormData} body - Request body.
     * @returns {Promise<object>}
     */
    async post(endpoint, body = {}) {
        const url = new URL(this.baseUrl + endpoint);
        const isFormData = body instanceof FormData;

        const options = {
            method: "POST",
            headers: {
                "X-WP-Nonce": this.nonce,
                "X-Requested-With": "XMLHttpRequest",
            },
            body: isFormData ? body : JSON.stringify(body),
        };

        if (!isFormData) {
            options.headers["Content-Type"] = "application/json";
        }

        return this.request(url, options);
    }

    /**
     * Runs fetch and normalizes API errors.
     *
     * @param {URL} url - Request URL.
     * @param {object} options - Fetch options.
     * @returns {Promise<object>}
     */
    async request(url, options = {}) {
        const response = await fetch(url.toString(), options);
        let responseData;

        try {
            responseData = await response.json();
        } catch (error) {
            if (response.ok) {
                throw error;
            }
            responseData = {};
        }

        if (!response.ok) {
            const message = responseData?.message || `HTTP error! Status: ${response.status}`;
            const error = new Error(message);
            error.response = responseData;
            throw error;
        }

        return responseData;
    }

    /**
     * Loads posts for the load-more flow.
     *
     * @param {string} postType - Post type.
     * @param {number} page - Page to load.
     * @param {object} extraParams - Extra query parameters.
     * @returns {Promise<object>}
     */
    postsLoadMore(postType, page, extraParams = {}) {
        const params = Object.assign({ post_type: postType, page: page }, extraParams);
        return this.get("posts-loader", params);
    }

    /**
     * Loads filtered posts.
     *
     * @param {string} postType - Post type.
     * @param {number} page - Page to load.
     * @param {object} filters - Filter data.
     * @returns {Promise<object>}
     */
    postsFilter(postType, page, filters = {}) {
        return this.post("posts-loader", {
            post_type: postType,
            page: page,
            filters: filters,
        });
    }

    /**
     * Loads services list by category ID.
     *
     * @param {number} categoryId - Selected category ID.
     * @returns {Promise<object>}
     */
    calcCostServices(categoryId) {
        return this.get("forms/calc-cost/services", { category_id: categoryId });
    }

    /**
     * Loads dynamic fields for a service.
     *
     * @param {number} serviceId - Selected service ID.
     * @returns {Promise<object>}
     */
    calcCostFields(serviceId) {
        return this.get("forms/calc-cost/fields", { service_id: serviceId });
    }

    /**
     * Generic form submit via REST API
     *
     * @param {FormData} formData
     * @returns {Promise<object>}
     */
    async submitForm(formData) {
        return this.post("forms/submit", formData);
    }
}

export const api = new ApiService(agds.rest.url, agds.rest.nonce);
